import React, { useEffect, useState, useRef } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Button, Form, Image, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import { Formik, Form as FormMik } from 'formik';
import {
  BsFillPersonFill,
  BsFillEnvelopeFill,
  BsChatQuoteFill,
} from 'react-icons/bs';

// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns';
import api from '../services/apiClient';
import { formatLocalDateTime } from '../utils/dateTime';

import MessageContainer from '../components/Message/MessageContainer';

import { Message, MessageGroup, MessageGeneric } from '../interfaces/IMessage';
import ConversaGrupoBIcon from '../assets/icon/conversaGrupoB.svg';
import ConversaGrupoIcon from '../assets/icon/conversaGrupo.svg';
import ConversaUserIcon from '../assets/icon/conversaUser.svg';
import ConversaUserBIcon from '../assets/icon/conversaUserB.svg';
import ChatIcon from '../assets/icon/mensagem.svg';
import FiltroIcon from '../assets/icon/filtro.svg';
import FiltroHoverIcon from '../assets/icon/filtroHover.svg';
import iconSearch from '../assets/icon/search.svg';

import IGroup from '../interfaces/IGroup';

interface DataRow {
  seqGrupoConversa: number;
  seqGrupo: number;
  dataConversa: string;
  nomeGrupo: string;
  nomeUsuario: string;
}

interface Messages {
  seqAtendimento: number;
  messages: Message[];
}

interface MessagesGroup {
  seqGrupoConversa: number;
  messages: MessageGroup[];
}

interface Users {
  seqUsuario: number;
  nome: string;
}

interface FormField {
  periodoInicial: string;
  periodoFinal: string;
  seqUsuario: number;
  nomeCliente: string;
  nomeClinica: string;
  produto: string;
  seqGrupo: number;
  conteudoMensagem: string;
  atendimentoCliente: boolean;
}

const dataFormatada = format(new Date(), 'yyyy-MM-dd');

const initialValues: FormField = {
  periodoInicial: dataFormatada,
  periodoFinal: dataFormatada,
  seqUsuario: 0,
  seqGrupo: 0,
  nomeCliente: '',
  nomeClinica: '',
  produto: '',
  conteudoMensagem: '',
  atendimentoCliente: false,
};

const ChatHistory: React.FC<any> = (): any => {
  const [data, setData] = React.useState<DataRow[]>([] as DataRow[]);
  const [nomeCliente, setNomeCliente] = useState('');
  const [historicoConversa, setHistoricoConversa] = useState<Messages>(
    {} as Messages
  );
  const [exibeFiltro, setExibeFiltro] = useState<boolean>(false);
  const [usuario, setUsuario] = useState<Users[]>([]);
  const [historicoConversaGrupo, setHistoricoConversaGrupo] =
    useState<MessagesGroup>({} as MessagesGroup);
  const [grupo, setGrupo] = useState<IGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelected, setRowSelected] = useState(0);
  const [searchTextMessage, setSearchTextMessage] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  /** ALTERAÇÃO: Adicionado useRef para armazenar a referência ao setValues do Formik, permitindo atualizar os valores do form dinamicamente após o mount. Motivo: Precisamos carregar os filtros salvos no localStorage e aplicá-los ao Formik após o componente montar, sem recriar o Formik. */
  const formikRef = useRef<any>(null);

  const showIconConversa = (msg: any): string => {
    if (rowSelected === msg.seqGrupoConversa) {
      if (msg.seqGrupo === 0) return ConversaUserIcon;

      return ConversaGrupoIcon;
    }
    if (msg.seqGrupo === 0) return ConversaUserBIcon;
    return ConversaGrupoBIcon;
  };

  const handleRowClicked = (row: DataRow): any => {
    const token = localStorage.getItem('@LiveChat:token');
    setNomeCliente(row.seqGrupo === 0 ? row.nomeUsuario : row.nomeGrupo);
    api
      .get(`api/MensagemGrupo/${row.seqGrupoConversa}/N`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const msg: MessagesGroup = {
          messages: response.data.data,
          seqGrupoConversa: row.seqGrupoConversa,
        };
        setHistoricoConversaGrupo(msg);
        setHistoricoConversa({} as Messages);
      });
  };

  const loadGrupo = (): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('grupo', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;
        if (result.success) {
          setGrupo(result.data);
        }
      });
  };

  const validationSchema = Yup.object().shape({
    periodoInicial: Yup.date().max(
      Yup.ref('periodoFinal'),
      'Data Inicial inválida.'
    ),
    periodoFinal: Yup.date().min(
      Yup.ref('periodoInicial'),
      'Data Inicial inválida.'
    ),
  });

  const getHistoricoChat = (dataFilter: any): void => {
    const token = localStorage.getItem('@LiveChat:token');

    setIsLoading(true);

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    const param = {
      ...dataFilter,
      periodoInicial:
        dataFilter.periodoInicial === '' ? null : dataFilter.periodoInicial,
      periodoFinal:
        dataFilter.periodoFinal === '' ? null : dataFilter.periodoFinal,
      conteudoMensagem:
        dataFilter.conteudoMensagem === ''
          ? ''
          : `%${dataFilter.conteudoMensagem}%`,
    };

    api
      .post('/ChatHistorico', param, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal,
      })
      .then((response) => {
        const { data: dataClients } = response.data;

        if (dataClients !== undefined) {
          setData(dataClients);
          setIsLoading(false);
          /** ALTERAÇÃO: Após carregar os dados da tabela, se houver um rowSelected salvo, encontre a row correspondente e chame handleRowClicked para restaurar a conversa selecionada. Motivo: Para restaurar não só os filtros, mas também o registro selecionado ao recarregar a página. */
          if (rowSelected) {
            const selectedRow = dataClients.find(
              (row: DataRow) => row.seqGrupoConversa === rowSelected
            );
            if (selectedRow) {
              handleRowClicked(selectedRow);
            }
          }
        }
      });

    /** ALTERAÇÃO: Salvar os filtros no localStorage dentro de getHistoricoChat. Motivo: Como a maioria das alterações de filtro chama getHistoricoChat imediatamente, isso persiste as mudanças. Incluído exibeFiltro para persistir o estado do filtro expandido. */
    localStorage.setItem(
      '@LiveChat:chatHistoryFilters',
      JSON.stringify({
        filters: dataFilter,
        rowSelected,
        exibeFiltro,
      })
    );
  };

  // Cleanup function to cancel any pending request when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('@LiveChat:token');

    /** ALTERAÇÃO: Removido o getHistoricoChat(initialValues) daqui, pois agora ele é chamado após carregar os filtros salvos no useEffect abaixo. Motivo: Garantir que os filtros salvos sejam aplicados antes de buscar os dados iniciais. */
    loadGrupo();

    api
      .get('/api/user', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: users } = response.data;
        setUsuario(users);
      });
  }, []);

  /** ALTERAÇÃO: Adicionado useEffect para carregar os filtros e rowSelected do localStorage ao montar o componente. Motivo: Restaurar o estado anterior (filtros e seleção) quando o usuário retorna à página. Adicionado carregamento de exibeFiltro e lógica para mostrá-lo se conteudoMensagem não for vazio. */
  useEffect(() => {
    const savedData = localStorage.getItem('@LiveChat:chatHistoryFilters');
    if (savedData && formikRef.current) {
      const {
        filters,
        rowSelected: savedRowSelected,
        exibeFiltro: savedExibeFiltro,
      } = JSON.parse(savedData);
      formikRef.current.setValues(filters);
      setRowSelected(savedRowSelected || 0);
      setExibeFiltro(savedExibeFiltro || filters.conteudoMensagem !== '');
      setSearchTextMessage(filters.conteudoMensagem);
      getHistoricoChat(filters);
    } else {
      getHistoricoChat(initialValues);
    }
  }, []);

  return (
    <div className="d-flex flex-column h-100 ">
      <h1 className="fw-bold ms-2">Histórico de Conversa</h1>
      <div className="d-flex w-100" style={{ flex: 1 }}>
        <div className="me-2" style={{ width: '60%' }}>
          <section className="ms-2">
            <Formik
              innerRef={
                formikRef
              } /** ALTERAÇÃO: Adicionado innerRef para acessar as funções do Formik (como setValues) de fora. Motivo: Permitir atualizar os valores do form dinamicamente ao carregar do localStorage. */
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                getHistoricoChat(values);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
                resetForm,
              }) => (
                <FormMik>
                  <div className="d-flex justify-content-end">
                    <Button
                      className="p-1 border-0 inputBox"
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        backgroundColor: exibeFiltro ? '#0075FF' : '#fff',
                        color: exibeFiltro ? '#fff' : '#000',
                      }}
                      onClick={() => {
                        const newExibe = !exibeFiltro;
                        setExibeFiltro(newExibe);
                        /** ALTERAÇÃO: Adicionado salvamento no localStorage ao alternar exibeFiltro. Motivo: Persistir o estado de exibição do filtro expandido. Usa formikRef para obter os valores atuais. */
                        localStorage.setItem(
                          '@LiveChat:chatHistoryFilters',
                          JSON.stringify({
                            filters: formikRef.current
                              ? formikRef.current.values
                              : initialValues,
                            rowSelected,
                            exibeFiltro: newExibe,
                          })
                        );
                      }}
                    >
                      Mais filtros
                      <Image
                        src={exibeFiltro ? FiltroHoverIcon : FiltroIcon}
                        alt="FilterIcon"
                        className="px-2"
                      />
                    </Button>
                  </div>
                  <div className="d-flex justify-content-center align-items-center">
                    <Form.Group className="p-0">
                      <Form.Label
                        htmlFor="inputPeriodoInicial"
                        className="mb-0"
                      >
                        Data Inicial
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="periodoInicial"
                        max="2999-12-31"
                        className={
                          (touched.periodoInicial || touched.periodoFinal) &&
                          errors.periodoFinal
                            ? 'is-invalid'
                            : 'p-0 inputBox'
                        }
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            periodoInicial: e.target.value,
                          });
                        }}
                        onBlur={handleBlur}
                        value={values.periodoInicial}
                      />
                      {touched.periodoFinal && errors.periodoFinal ? (
                        <div className="invalid-feedback">
                          {errors.periodoFinal}
                        </div>
                      ) : null}
                    </Form.Group>

                    <Form.Group className="p-0 ms-2">
                      <Form.Label htmlFor="inputPeriodoFinal" className="mb-0">
                        Data Final
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="periodoFinal"
                        max="2999-12-31"
                        className="p-0 inputBox"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            periodoFinal: e.target.value,
                          });
                        }}
                        onBlur={handleBlur}
                        value={values.periodoFinal}
                      />
                    </Form.Group>

                    <Form.Group className="ms-2 w-50">
                      <Form.Label className="mb-0">Usuário</Form.Label>
                      <Form.Select
                        size="sm"
                        name="seqUsuario"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            seqUsuario: e.target.value,
                          });
                        }}
                        onBlur={handleBlur}
                        value={values.seqUsuario}
                        className="inputBox"
                      >
                        <option value={0}>Todos</option>
                        {usuario.map((user) => (
                          <option key={user.seqUsuario} value={user.seqUsuario}>
                            {user.nome}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="ms-2 w-50">
                      <Form.Label className="mb-0">Grupo</Form.Label>
                      <Form.Select
                        size="sm"
                        name="seqGrupo"
                        onChange={(e) => {
                          handleChange(e);
                          getHistoricoChat({
                            ...values,
                            seqGrupo: e.target.value,
                          });
                        }}
                        onBlur={handleBlur}
                        value={values.seqGrupo}
                        className="inputBox"
                      >
                        <option value={0}>Todos</option>
                        {grupo.map((grp) => (
                          <option key={grp.seqGrupo} value={grp.seqGrupo}>
                            {grp.nomeGrupo}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div
                    className={`d-flex align-items-end ${
                      exibeFiltro ? '' : 'd-none'
                    }`}
                  >
                    <Form.Group className="ms-2 w-50">
                      <Form.Label>Conteúdo da Mensagem</Form.Label>
                      <Form.Control
                        type="text"
                        name="conteudoMensagem"
                        className="mb-2 inputBox"
                        value={values.conteudoMensagem}
                        onChange={(e) => {
                          handleChange(e);
                          setSearchTextMessage(e.target.value);
                          /** ALTERAÇÃO: Adicionado salvamento no localStorage no onChange de conteudoMensagem. Motivo: Como este campo não chama getHistoricoChat automaticamente, persistir mudanças imediatas para restaurar ao retornar. Usa {...values, ...} pois values ainda não está atualizado. */
                          localStorage.setItem(
                            '@LiveChat:chatHistoryFilters',
                            JSON.stringify({
                              filters: {
                                ...values,
                                conteudoMensagem: e.target.value,
                              },
                              rowSelected,
                              exibeFiltro,
                            })
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            getHistoricoChat(values);
                          }
                        }}
                      />
                    </Form.Group>
                    <Button
                      className="ms-3 mb-2"
                      style={{ height: '2.75rem' }}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onClick={() => {
                        getHistoricoChat(values);
                      }}
                    >
                      <Image
                        src={iconSearch}
                        alt="search"
                        style={{ height: '1.75rem', width: '1.75rem' }}
                      />
                    </Button>
                  </div>
                </FormMik>
              )}
            </Formik>
          </section>
          <section
            className="card position-static ms-2 mt-3  overflow-hidden position-relative"
            style={{
              borderRadius: '0.68rem',
              height: `Calc(100vh - ${exibeFiltro ? '27rem' : '22rem'})`,
              boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Spinner overlay */}
            {isLoading && ( // Substitua isLoading pela sua variável booleana
              <div
                className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1030,
                }}
              >
                <Spinner
                  style={{ width: '3rem', height: '3rem' }}
                  animation="border"
                  variant="primary"
                />
              </div>
            )}
            <table className="table table-nowrap mb-0 h-100">
              <thead
                className=" fw-light text-dark"
                style={{
                  backgroundColor: '#D9D9D9',
                  borderRadius: '0.68rem!important',
                  fontSize: '1.25rem',
                }}
              >
                <tr style={{ fontSize: '1.125rem', fontWeight: '600' }}>
                  <th className="col"> </th>
                  <th className="col-2">Data</th>
                  <th className="col-5">Grupo</th>
                  <th className="col-4">Usuario</th>
                </tr>
              </thead>
              <tbody
                className="text-dark"
                style={{ borderColor: 'transparent', fontSize: '1.25rem' }}
              >
                <tr className="h-100">
                  <td colSpan={4}>
                    <div className="table-responsive h-100">
                      <table
                        className="table table-responsive table-hover w-100"
                        style={{
                          borderColor: 'transparent',
                          fontSize: '1.25rem',
                        }}
                      >
                        <tbody
                          className="overflow-auto"
                          style={{ height: '100px' }}
                        >
                          {data.map((msg) => (
                            <tr
                              key={msg.seqGrupoConversa}
                              onClick={() => {
                                /** ALTERAÇÃO: Adicionado salvamento no localStorage ao selecionar uma row. Motivo: Garantir que o rowSelected seja persistido imediatamente ao clicar, para restaurar na próxima visita. Incluído exibeFiltro. */
                                setRowSelected(msg.seqGrupoConversa);
                                localStorage.setItem(
                                  '@LiveChat:chatHistoryFilters',
                                  JSON.stringify({
                                    filters: formikRef.current
                                      ? formikRef.current.values
                                      : initialValues,
                                    rowSelected: msg.seqGrupoConversa,
                                    exibeFiltro,
                                  })
                                );
                                handleRowClicked(msg);
                              }}
                              style={{
                                fontSize: '1.125rem',
                                fontWeight: '600',
                                backgroundColor:
                                  rowSelected === msg.seqGrupoConversa
                                    ? '#0075FF'
                                    : '#FFF',
                                color:
                                  rowSelected === msg.seqGrupoConversa
                                    ? '#FFF'
                                    : '#000',
                              }}
                            >
                              <td className="col ps-2">
                                <Image
                                  src={showIconConversa(msg)}
                                  className="ms-2 me-1"
                                  style={{
                                    width: '1.56rem',
                                    height: '1.25rem',
                                  }}
                                />
                              </td>
                              <td className="col-2">
                                {formatLocalDateTime(
                                  msg.dataConversa,
                                  'dd/MM/yyyy HH:mm'
                                )}
                              </td>
                              <td className="col-5">{msg.nomeGrupo}</td>
                              <td className="col-4">{msg.nomeUsuario}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
        <section
          className="d-flex flex-column ms-2 mb-2"
          style={{
            width: '40%',

            borderRadius: '0.68rem',
            backgroundColor: '#FFF',
            fontSize: '1.25rem',
            boxShadow: '0px 4px 6px 3px rgba(0, 0, 0, 0.25)',
            flex: 1,
          }}
        >
          <div
            className="d-flex align-items-center ps-3"
            style={{
              borderTopLeftRadius: '0.68rem',
              borderTopRightRadius: '0.68rem',
              borderColor: 'transparent',
              backgroundColor: '#1A1A1A',
              fontSize: '1.25rem',
            }}
          >
            <Image
              style={{ height: '1.8rem', width: '2rem' }}
              src={ChatIcon}
              alt="Mensagens"
            />
            <span
              style={{
                color: '#fff',
                fontSize: '1.58rem',
                marginLeft: '0.6rem',
              }}
            >
              {`Chat - ${nomeCliente}`}
            </span>
          </div>
          <div
            className="d-flex flex-column px-1"
            style={{
              borderBottomLeftRadius: '0.68rem',
              borderBottomRightRadius: '0.68rem',
              flex: 1,
            }}
          >
            <MessageContainer
              messages={
                historicoConversa.seqAtendimento === undefined
                  ? undefined
                  : historicoConversa
              }
              messagesGroup={
                historicoConversaGrupo.seqGrupoConversa === undefined
                  ? undefined
                  : historicoConversaGrupo
              }
              readOnly
              searchTextMessage={searchTextMessage}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default ChatHistory;
